import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
    InputOTPSeparator,
} from "@/ui/input-otp"

import { Button } from "@/ui/button"
import FormHeading from "../../components/FormHeading/FormHeading"
import PlatformBranding from "../../components/PlatformBranding/PlatformBranding"
import { useUIConfig } from "../../hooks/useUIConfig"
import { toast } from 'sonner'
import { useVerifyOTP } from "../../hooks/useVerifyOTP"
import { useSendOTP } from "../../hooks/useSendOTP"
import { setAuthTokens } from "@/shared/services/http"
import styles from "./OTP.module.scss"
import { MaterialIcon } from "@/ui/material-icon"

const OTP = () => {
    const { data: uiConfig, isLoading, error } = useUIConfig()
    const [otp, setOtp] = useState("")
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const verifyOTP = useVerifyOTP()
    const sendOTP = useSendOTP()
    const hasSentOTP = useRef(false)

    const state = location.state as { identifier?: string; fromAccountDisabled?: boolean } | null
    const identifier = state?.identifier || ''
    const fromAccountDisabled = state?.fromAccountDisabled ?? false
    const purpose = fromAccountDisabled ? 'LOGIN' : 'PASSWORD_RESET'

    // Auto-send OTP when user lands from "Account disabled" flow
    useEffect(() => {
        if (identifier && fromAccountDisabled && !hasSentOTP.current) {
            hasSentOTP.current = true
            sendOTP.mutate(
                { identifier, channel: 'EMAIL', purpose: 'LOGIN' },
                {
                    onSuccess: () => {
                        toast.success('OTP sent to your email. Please check your inbox.')
                    },
                    onError: (err: any) => {
                        const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to send OTP'
                        toast.error(msg)
                    },
                }
            )
        }
    }, [identifier, fromAccountDisabled])

    useEffect(() => {
        if (otp.length === 6 && !loading) {
            // optional auto-validation behavior - none
        }
    }, [otp, loading])

    const handleVerify = async () => {
        if (!identifier) {
            toast.error('Missing identifier. Please start from Forgot Password flow.')
            navigate('/forgot-password')
            return
        }

        if (otp.length < 6) return

        setLoading(true)

        try {
            const response: any = await verifyOTP.mutateAsync({
                identifier,
                otpCode: otp,
                purpose,
            })

            if (response?.success) {
                if (purpose === 'LOGIN') {
                    // Handle multiple response shapes: data.tokens, data, or root level
                    const tokens = response?.data?.tokens ?? response?.data ?? response
                    const accessToken = tokens?.access_token ?? tokens?.accessToken
                    if (accessToken) {
                        const expiresIn = tokens?.expires_in ?? tokens?.expiresIn ?? 3600
                        const refreshToken = tokens?.refresh_token ?? tokens?.refreshToken
                        const refreshExpiresIn = tokens?.refresh_expires_in ?? tokens?.refreshExpiresIn
                        setAuthTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn)
                    }
                    toast.success('OTP verified successfully.')
                    navigate('/dashboard', { replace: true })
                } else {
                    toast.success('OTP verified. Please reset your password.')
                    navigate('/reset-password', { state: { resetToken: response.data?.reset_token } })
                }
            } else {
                toast.error(response?.message || 'OTP verification failed')
            }
        } catch (err: any) {
            toast.error(err?.message || 'OTP verification error')
        } finally {
            setLoading(false)
        }
    }

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loader}>
                    <div className={styles.spinner}></div>
                    <p>Loading...</p>
                </div>
            </div>
        )
    }

    if (error) return <div>Error loading config</div>

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div className={styles.left}>
                    <PlatformBranding
                        logo={uiConfig?.data?.logo}
                        platformName={uiConfig?.data?.ui_config.platform_name}
                        slogan={uiConfig?.data?.ui_config.platform_slogan}
                    />
                </div>
                <div className={styles.right}>
                    <div className={styles.card}>
                        <FormHeading
                            title="OTP"
                            subtitle={fromAccountDisabled ? "Enter the code sent to your email to verify your account." : "Enter the code from your text message / email to reset password."}
                            as="h1"
                            align="center"
                        />

                        <div className={styles.otpArea}>
                            <InputOTP
                                maxLength={6}
                                value={otp}
                                onChange={(value) => setOtp(value)}
                            >
                                <InputOTPGroup>
                                    {Array.from({ length: 6 }).map((_, index) => (
                                        <>
                                            <InputOTPSlot key={`slot-${index}`} index={index} />
                                            {index === 2 && <InputOTPSeparator key="sep-middle" />}
                                        </>
                                    ))}
                                </InputOTPGroup>
                            </InputOTP>
                        </div>

                        <Button
                            className={styles.submitButton}
                            onClick={handleVerify}
                            disabled={otp.length < 6 || loading}
                        >
                            {loading ? "Verifying..." : "Verify and Continue"}
                        </Button>

                        <Link
                            to={"/"}
                            className={styles.loginLink}
                        >
                            <MaterialIcon name="arrow_back" size={20} />
                            Back
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OTP;